export const colors = {
  ink: {
    paper: '#FFFFFF',
    paperDark: '#F3F7F4',
    paperWarm: '#FFF9EE',
    press: '#16241C',
    pressMuted: '#5E6A62',
    acid: '#1F7A4D',
    acidPressed: '#18603D',
    field: '#115035',
    fieldDeep: '#113925',
    maroon: '#721428',
  },

  // Primary palette
  primary: {
    50: '#F2F8F4',
    100: '#DCEFE3',
    200: '#B7DEC7',
    300: '#84C59E',
    400: '#4BA873',
    500: '#1F7A4D',
    600: '#18603D',
    700: '#174C33',
    800: '#16241C',
    900: '#0D1511',
  },

  // Success/Green
  success: {
    50: '#F2F8F4',
    100: '#DCEFE3',
    200: '#B7DEC7',
    300: '#84C59E',
    400: '#4BA873',
    500: '#1F7A4D',
    600: '#18603D',
    700: '#174C33',
    800: '#143B29',
    900: '#0F2A1F',
  },

  // Error/Red
  error: {
    50: '#FBF0ED',
    100: '#F2D7D1',
    200: '#E7B0A6',
    300: '#DA7F70',
    400: '#C2452F',
    500: '#A93A29',
    600: '#913124',
    700: '#76271E',
    800: '#5A1E17',
    900: '#3E1511',
  },

  // Warning/Amber
  warning: {
    50: '#FBF4E5',
    100: '#EFDDB4',
    200: '#E1C071',
    300: '#D39F32',
    400: '#C68A1E',
    500: '#A87319',
    600: '#8D5F15',
    700: '#704B11',
    800: '#54380D',
    900: '#382509',
  },

  // Neutral/Gray
  neutral: {
    50: '#FFFFFF',
    100: '#F3F7F4',
    200: '#E5ECE7',
    300: '#D2DDD6',
    400: '#A7B4AC',
    500: '#7D897F',
    600: '#5E6A62',
    700: '#3D4941',
    800: '#253129',
    900: '#16241C',
  },

  // Semantic colors
  background: '#F4F3EC',
  card: '#FFFEFA',
  cardBorder: '#D9DED8',

  text: {
    primary: '#16241C',
    secondary: '#5E6A62',
    tertiary: '#3D4941',
    disabled: '#A7B4AC',
    inverse: '#FFFFFF',
  },

  interactive: {
    primary: '#1F7A4D',
    primaryHover: '#18603D',
    primaryActive: '#16241C',
    secondary: '#16241C',
    secondaryHover: '#253129',
    danger: '#C2452F',
    dangerHover: '#A93A29',
  },

  state: {
    neutralBg: '#E8E8E8',
    success: '#1F7A4D',
    successBg: '#F2F8F4',
    successBorder: '#B7DEC7',
    error: '#C2452F',
    errorBg: '#FBF0ED',
    errorBorder: '#E7B0A6',
    warning: '#C68A1E',
    warningBg: '#FBF4E5',
    warningBorder: '#E1C071',
    info: '#3F6E7D',
    infoBg: '#EEF5F6',
    infoBorder: '#B5CDD3',
  },
} as const;

export const semanticColors = {
  light: {
    background: colors.background,
    foreground: colors.text.primary,
    card: colors.card,
    cardForeground: colors.text.primary,
    popover: colors.card,
    popoverForeground: colors.text.primary,
    primary: colors.primary[500],
    primaryForeground: colors.text.primary,
    secondary: colors.text.primary,
    secondaryForeground: colors.text.primary,
    muted: colors.neutral[100],
    mutedForeground: colors.text.secondary,
    accent: colors.primary[500],
    accentForeground: colors.text.primary,
    destructive: colors.error[500],
    destructiveForeground: colors.text.inverse,
    border: colors.cardBorder,
    input: colors.cardBorder,
    ring: colors.primary[500],
  },
  dark: {
    background: '#020817',
    foreground: '#F8FAFC',
    card: '#020817',
    cardForeground: '#F8FAFC',
    popover: '#020817',
    popoverForeground: '#F8FAFC',
    primary: '#3B82F6',
    primaryForeground: '#020817',
    secondary: '#1E293B',
    secondaryForeground: '#F8FAFC',
    muted: '#1E293B',
    mutedForeground: '#94A3B8',
    accent: '#1E293B',
    accentForeground: '#F8FAFC',
    destructive: '#7F1D1D',
    destructiveForeground: '#F8FAFC',
    border: '#1E293B',
    input: '#1E293B',
    ring: '#E4EAFB',
  },
} as const;

// Spacing Scale (in pixels) - Base 4px system
export const spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  base: 16,
  lg: 20,
  xl: 24,
  '2xl': 32,
  '3xl': 40,
  '4xl': 48,
  '5xl': 64,
} as const;

// Typography Scale
export const typography = {
  fontFamily: {
    display: 'Archivo_900Black',
    body: 'SpaceGrotesk_400Regular',
    bodyMedium: 'SpaceGrotesk_600SemiBold',
    mono: 'IBMPlexMono_600SemiBold',
    fallback: 'System',
  },

  fontSize: {
    xs: 10,
    sm: 12,
    base: 14,
    md: 17,
    lg: 18,
    xl: 20,
    '2xl': 26,
    '3xl': 34,
    '4xl': 44,
    '5xl': 56,
  },

  fontWeight: {
    normal: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
    extrabold: '800',
  },

  lineHeight: {
    tight: 1.2,
    normal: 1.5,
    relaxed: 1.75,
  },
} as const;

// Border Radius
export const borderRadius = {
  none: 0,
  sm: 4,
  base: 8,
  md: 8,
  lg: 12,
  xl: 12,
  '2xl': 16,
  full: 999,
} as const;

// Shadow/Elevation System
export const shadows = {
  none: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
    elevation: 0,
  },
  sm: {
    shadowColor: colors.ink.press,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.06,
    shadowRadius: 12,
    elevation: 1,
  },
  base: {
    shadowColor: colors.ink.press,
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.08,
    shadowRadius: 16,
    elevation: 2,
  },
  md: {
    shadowColor: colors.ink.press,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.08,
    shadowRadius: 20,
    elevation: 2,
  },
  lg: {
    shadowColor: colors.ink.press,
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.1,
    shadowRadius: 24,
    elevation: 3,
  },
  xl: {
    shadowColor: colors.ink.press,
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.12,
    shadowRadius: 28,
    elevation: 4,
  },
} as const;

// Animation Timings
export const animations = {
  duration: {
    fast: 120,
    base: 160,
    normal: 200,
    slow: 300,
    slower: 400,
  },

  easing: {
    linear: 'linear',
    easeIn: 'ease-in',
    easeOut: 'ease-out',
    easeInOut: 'ease-in-out',
  },
} as const;

// Opacity values
export const opacity = {
  disabled: 0.5,
  hover: 0.8,
  active: 0.7,
  subtle: 0.6,
} as const;

export const tokens = {
  colors,
  semanticColors,
  spacing,
  typography,
  borderRadius,
  shadows,
  animations,
  opacity,
} as const;

export type Tokens = typeof tokens;
